import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowUpRight, LogOut, Trash2, RefreshCw, Mail, Phone, Building2, Tag, Wallet,
  MessageSquare, Search, KeyRound, X, Inbox, Briefcase, BookOpen, Users,
  LayoutDashboard, UserCircle2, Truck, FolderKanban, ListChecks, Coins, Receipt,
  PanelLeftClose, PanelLeftOpen, Image as ImageIcon, FileText, History, Wrench, AlertTriangle,
  Shield, ReceiptIndianRupee,
} from "lucide-react";
import Logo from "@/components/Logo";
import CrudPanel from "@/components/admin/CrudPanel";
import ClientLogos from "@/components/admin/ClientLogos";
import Applications from "@/components/admin/Applications";
import {
  PROJECT_FIELDS, ARTICLE_FIELDS, ROLE_FIELDS,
  PROJECT_OPS, ARTICLE_OPS, ROLE_OPS,
} from "@/components/admin/cmsConfig";
import Analytics from "@/components/admin/crm/Analytics";
import Leads from "@/components/admin/crm/Leads";
import Vendors from "@/components/admin/crm/Vendors";
import CrmProjects from "@/components/admin/crm/CrmProjects";
import Tasks from "@/components/admin/crm/Tasks";
import ProjectPayments from "@/components/admin/crm/ProjectPayments";
import ReachvelPayments from "@/components/admin/crm/ReachvelPayments";
import PaymentReceipts from "@/components/admin/crm/PaymentReceipts";
import NonProjectPayments from "@/components/admin/crm/NonProjectPayments";
import MaintenanceProjects from "@/components/admin/crm/MaintenanceProjects";
import ChangeRequests from "@/components/admin/crm/ChangeRequests";
import Clients from "@/components/admin/Clients";
import AuditLog from "@/components/admin/AuditLog";
import AdminUsers from "@/components/admin/AdminUsers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "reachvel_admin_token";
const TAB_KEY = "reachvel_admin_tab";

// Sidebar groups
const NAV_GROUPS = [
  {
    label: "Website CMS",
    items: [
      { k: "briefings", label: "Briefings", icon: Inbox },
      { k: "projects",  label: "Projects",  icon: Briefcase },
      { k: "knowledge", label: "Knowledge", icon: BookOpen },
      { k: "careers",   label: "Careers",   icon: Users },
      { k: "applications", label: "Applications", icon: FileText },
      { k: "client-logos", label: "Client Logos", icon: ImageIcon },
    ],
  },
  {
    label: "Reachvel Dashboard",
    items: [
      { k: "crm-analytics",        label: "Analytics",         icon: LayoutDashboard },
      { k: "crm-leads",            label: "Leads",             icon: UserCircle2 },
      { k: "crm-vendors",          label: "Vendors",           icon: Truck },
      { k: "crm-projects",         label: "Reachvel Projects", icon: FolderKanban },
      { k: "crm-maintenance",      label: "Maintenance",       icon: Wrench },
      { k: "crm-change-requests",  label: "Change Requests",   icon: AlertTriangle },
      { k: "crm-tasks",            label: "Tasks",             icon: ListChecks },
      { k: "crm-project-payments", label: "Project Payments",  icon: Coins },
      { k: "crm-non-project-payments", label: "Non-Project Payments", icon: ReceiptIndianRupee },
      { k: "crm-payment-receipts", label: "Payment Receipts",  icon: FileText },
      { k: "crm-reachvel-payments",label: "Reachvel Payments", icon: Receipt },
      { k: "clients",              label: "Clients",           icon: UserCircle2 },
      { k: "audit-log",            label: "Audit Log",         icon: History },
    ],
  },
  {
    label: "System",
    // superadmin-only — filtered at render time using currentUser.role
    superadminOnly: true,
    items: [
      { k: "admin-users", label: "Admin Users", icon: Shield },
    ],
  },
];

const ALL_TABS = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.k));
const LABEL_FOR = Object.fromEntries(NAV_GROUPS.flatMap((g) => g.items.map((i) => [i.k, i.label])));

const STATUSES = [
  { k: "new",        label: "New",        dot: "bg-[#ff5722]"   },
  { k: "reviewing",  label: "Reviewing",  dot: "bg-amber-500"   },
  { k: "contacted",  label: "Contacted",  dot: "bg-blue-500"    },
  { k: "won",        label: "Won",        dot: "bg-emerald-500" },
  { k: "lost",       label: "Lost",       dot: "bg-zinc-400"    },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.k, s]));

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [username, setUsername] = useState(() => localStorage.getItem("reachvel_admin_username") || "");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  // Current logged-in admin user (id, username, name, role). Populated from
  // the /admin/users/me endpoint after login (or on page reload with a valid token).
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("reachvel_admin_user") || "null"); }
    catch { return null; }
  });

  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ total: 0, today: 0, by_status: {} });
  const [loading, setLoading] = useState(false);

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [tab, setTab] = useState(() => {
    const saved = localStorage.getItem(TAB_KEY);
    return saved && ALL_TABS.includes(saved) ? saved : "briefings";
  });
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") return window.innerWidth >= 768;
    return true;
  });

  useEffect(() => {
    localStorage.setItem(TAB_KEY, tab);
    // If a founders-office user is somehow on the admin-users tab, bump them off.
    if (tab === "admin-users" && currentUser && currentUser.role !== "superadmin") {
      setTab("briefings");
    }
  }, [tab, currentUser]);

  const debounceRef = useRef(null);

  const fetchBriefings = async (t = token, query = q, filter = statusFilter) => {
    if (!t) return;
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (filter && filter !== "all") params.status = filter;
      const [s, st] = await Promise.all([
        axios.get(`${API}/admin/submissions`, { headers: { "X-Admin-Token": t }, params }),
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Token": t } }),
      ]);
      setSubmissions(s.data || []);
      setStats(st.data || { total: 0, today: 0, by_status: {} });
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        hardLogout();
      } else {
        toast.error("Couldn't load submissions.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Reachvel · Admin";
    if (token) {
      fetchBriefings(token, "", "all");
      // Sync currentUser from backend so role/name is fresh
      axios.get(`${API}/admin/users/me`, { headers: { "X-Admin-Token": token } })
        .then(({ data }) => {
          if (data && data.id) {
            setCurrentUser(data);
            localStorage.setItem("reachvel_admin_user", JSON.stringify(data));
          }
        })
        .catch(() => { /* legacy token still works via require_admin fallback */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // debounced refresh on q/statusFilter change (only for briefings)
  useEffect(() => {
    if (!token || tab !== "briefings") return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchBriefings(token, q, statusFilter), 280);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, statusFilter, tab]);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    try {
      setLoggingIn(true);
      const { data } = await axios.post(`${API}/admin/login`, {
        username: (username || "").trim().toLowerCase(),
        password,
      });
      localStorage.setItem(TOKEN_KEY, data.token);
      if (username) localStorage.setItem("reachvel_admin_username", username.trim().toLowerCase());
      if (data.user) {
        localStorage.setItem("reachvel_admin_user", JSON.stringify(data.user));
        setCurrentUser(data.user);
      }
      setToken(data.token);
      setPassword("");
      await fetchBriefings(data.token, "", "all");
      toast.success(`Signed in as ${data.user?.name || data.user?.username || "admin"}`);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 429) toast.error(err.response.data?.detail || "Too many attempts.");
      else if (status === 401) toast.error("Invalid username or password");
      else toast.error("Sign-in failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const hardLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("reachvel_admin_user");
    setToken("");
    setCurrentUser(null);
    setSubmissions([]);
    setStats({ total: 0, today: 0, by_status: {} });
    setSelected(null);
    setPwModalOpen(false);
    setQ("");
    setStatusFilter("all");
  };

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { headers: { "X-Admin-Token": token } }); } catch { /* noop */ }
    hardLogout();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This can't be undone.")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { headers: { "X-Admin-Token": token } });
      toast.success("Deleted");
      setSubmissions((xs) => xs.filter((x) => x.id !== id));
      setSelected(null);
      fetchBriefings(token, q, statusFilter);
    } catch { toast.error("Couldn't delete"); }
  };

  const changeStatus = async (id, newStatus) => {
    const prev = submissions.find((s) => s.id === id)?.status;
    setSubmissions((xs) => xs.map((x) => (x.id === id ? { ...x, status: newStatus } : x)));
    if (selected?.id === id) setSelected({ ...selected, status: newStatus });
    try {
      await axios.patch(
        `${API}/admin/submissions/${id}`,
        { status: newStatus },
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(`Marked ${STATUS_MAP[newStatus].label}`);
      fetchBriefings(token, q, statusFilter);
    } catch {
      toast.error("Couldn't update status");
      setSubmissions((xs) => xs.map((x) => (x.id === id ? { ...x, status: prev } : x)));
    }
  };

  const numbered = useMemo(
    () => submissions.map((s, i) => ({ ...s, sno: i + 1 })),
    [submissions],
  );

  if (!token) {
    return (
      <div data-testid="page-admin-login" className="min-h-screen bg-[#050505] text-white flex flex-col">
        <header className="px-5 md:px-10 py-5 border-b border-white/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo theme="dark" className="h-9 md:h-10 w-auto" asLink={false} />
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#ff5722] pl-3 border-l border-white/20">admin</span>
          </Link>
          <Link to="/" className="text-sm text-white/60 hover:text-white link-underline" data-testid="admin-back-home">← Back to site</Link>
        </header>

        <div className="flex-1 flex items-center justify-center px-5 py-20">
          <form onSubmit={login} className="w-full max-w-md bg-[#0f0f0f] border border-white/10 p-8 md:p-10 rounded-sm">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">
              Admin · Access required
            </div>
            <h1 className="mt-3 font-display font-black text-3xl tracking-tighter">Sign in</h1>
            <p className="mt-3 text-sm text-white/60">
              Enter your credentials to access the Reachvel dashboard.
            </p>

            <div className="mt-8">
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Username</label>
              <input
                type="text"
                autoFocus
                autoComplete="username"
                data-testid="admin-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#ff5722] font-mono text-sm"
                placeholder="superadmin"
              />
            </div>
            <div className="mt-6">
              <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">Password</label>
              <input
                type="password"
                autoComplete="current-password"
                data-testid="admin-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-transparent border-b border-white/20 py-2 focus:outline-none focus:border-[#ff5722]"
                placeholder="••••••••"
              />
            </div>
            <button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loggingIn}
              className="btn-ghost-light !border-[#ff5722] !text-white bg-[#ff5722] hover:!bg-white hover:!text-black mt-8 w-full justify-center disabled:opacity-60"
            >
              {loggingIn ? "Signing in…" : "Sign in"} <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-admin-dashboard" className="min-h-screen bg-[#f7f6f3] flex">
      {/* Sidebar */}
      <aside
        className={`${sidebarOpen ? "translate-x-0" : "-translate-x-full md:w-0 md:border-r-0"}
                    fixed md:sticky top-0 left-0 z-40 h-screen w-72 shrink-0
                    bg-white border-r border-black/10 transition-all duration-300 flex flex-col overflow-hidden`}
      >
        <div className="px-5 py-5 border-b border-black/10 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo theme="light" className="h-9 w-auto" asLink={false} />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff5722] pl-3 border-l border-black/15">admin</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(false)}
            data-testid="admin-sidebar-close"
            className="h-8 w-8 rounded-full border border-black/10 flex items-center justify-center hover:bg-[#0a0a0a] hover:text-white transition-colors"
            title="Hide sidebar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-5 px-3">
          {NAV_GROUPS
            .filter((g) => !g.superadminOnly || currentUser?.role === "superadmin")
            .map((g) => (
            <div key={g.label} className="mb-6">
              <div className="px-3 mb-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a]/70">
                {g.label}
              </div>
              <ul className="space-y-1">
                {g.items.map(({ k, label, icon: Icon }) => (
                  <li key={k}>
                    <button
                      onClick={() => setTab(k)}
                      data-testid={`admin-nav-${k}`}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${
                        tab === k
                          ? "bg-[#0a0a0a] text-white"
                          : "text-[#0a0a0a] hover:bg-[#0a0a0a]/5"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="flex-1 text-left">{label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="px-5 py-4 border-t border-black/10 flex items-center gap-2">
          <button
            onClick={() => setPwModalOpen(true)}
            data-testid="admin-my-account"
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722] transition-colors"
          >
            <UserCircle2 className="h-3.5 w-3.5" /> My account
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout"
            className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 text-xs border border-black/15 rounded-full hover:border-[#0a0a0a] hover:bg-[#0a0a0a] hover:text-white transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Sidebar backdrop (mobile only — closes when clicked) */}
      {sidebarOpen && (
        <div onClick={() => setSidebarOpen(false)} className="md:hidden fixed inset-0 z-30 bg-black/40" />
      )}

      {/* Main area */}
      <div className="flex-1 min-w-0">
        {/* Topbar (always visible) — sidebar toggle + page title + refresh */}
        <header className="sticky top-0 z-20 bg-white border-b border-black/10 px-4 md:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen((v) => !v)}
              data-testid="admin-sidebar-toggle"
              className="h-9 w-9 shrink-0 rounded-full border border-black/15 flex items-center justify-center hover:border-[#ff5722] hover:text-[#ff5722] transition-colors"
              title={sidebarOpen ? "Hide sidebar" : "Show sidebar"}
              aria-label="Toggle sidebar"
            >
              {sidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            </button>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[#0a0a0a] truncate">{LABEL_FOR[tab] || "Admin"}</div>
              {currentUser && (
                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#4a4a4a] truncate">
                  {currentUser.name} · <span className={currentUser.role === "superadmin" ? "text-[#ff5722] font-bold" : "text-blue-700 font-bold"}>{currentUser.role === "superadmin" ? "Super Admin" : "Founder's Office"}</span>
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => tab === "briefings" ? fetchBriefings() : null}
            data-testid="admin-refresh-mobile"
            className="h-9 w-9 rounded-full border border-black/10 flex items-center justify-center hover:border-[#ff5722] hover:text-[#ff5722] transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </header>

        <main className="px-5 md:px-10 py-8 md:py-12 max-w-[1500px]">
          {/* Website CMS */}
          {tab === "briefings" && (
            <BriefingsView
              stats={stats}
              numbered={numbered}
              loading={loading}
              q={q}
              setQ={setQ}
              statusFilter={statusFilter}
              setStatusFilter={setStatusFilter}
              onOpen={setSelected}
              onChangeStatus={changeStatus}
            />
          )}

          {tab === "projects" && (
            <CrudPanel
              title="Projects"
              entityName="projects"
              fields={PROJECT_FIELDS}
              {...PROJECT_OPS(token)}
              rowLabel={(it) => it.title}
              rowSubtitle={(it) => `${it.client || ""} · ${it.year || ""} · ${it.domain || ""}`}
              rowMeta={(it) => `Order ${it.display_order} · /projects#${it.slug}`}
              emptyText="No projects yet. Click New to add your first case study."
            />
          )}

          {tab === "knowledge" && (
            <CrudPanel
              title="Knowledge Center"
              entityName="knowledge"
              fields={ARTICLE_FIELDS}
              {...ARTICLE_OPS(token)}
              rowLabel={(it) => `${it.featured ? "★ " : ""}${it.title}`}
              rowSubtitle={(it) => `${it.category || ""} · ${it.author || ""} · ${it.date || ""}`}
              rowMeta={(it) => `Order ${it.display_order} · /knowledge/${it.slug}`}
              emptyText="No essays yet. Click New to publish your first."
            />
          )}

          {tab === "careers" && (
            <CrudPanel
              title="Careers"
              entityName="careers"
              fields={ROLE_FIELDS}
              {...ROLE_OPS(token)}
              rowLabel={(it) => `${it.active ? "" : "○ "}${it.title}`}
              rowSubtitle={(it) => `${it.dept || ""} · ${it.location || ""} · ${it.type || ""}`}
              rowMeta={(it) => `Order ${it.display_order}${it.active ? "" : " · inactive"}`}
              emptyText="No roles yet. Click New to add an open position."
            />
          )}

          {tab === "client-logos" && <ClientLogos token={token} />}
          {tab === "applications" && <Applications token={token} />}

          {/* CRM */}
          {tab === "crm-analytics"         && <Analytics token={token} />}
          {tab === "crm-leads"             && <Leads token={token} />}
          {tab === "crm-vendors"           && <Vendors token={token} />}
          {tab === "crm-projects"          && <CrmProjects token={token} />}
          {tab === "crm-tasks"             && <Tasks token={token} />}
          {tab === "crm-project-payments"  && <ProjectPayments token={token} />}
          {tab === "crm-payment-receipts"  && <PaymentReceipts token={token} />}
          {tab === "crm-non-project-payments" && <NonProjectPayments token={token} />}
          {tab === "crm-maintenance"       && <MaintenanceProjects token={token} />}
          {tab === "crm-change-requests"   && <ChangeRequests token={token} currentUser={currentUser} />}
          {tab === "crm-reachvel-payments" && <ReachvelPayments token={token} />}
          {tab === "clients"               && <Clients token={token} />}
          {tab === "audit-log"             && <AuditLog token={token} />}
          {tab === "admin-users"           && <AdminUsers token={token} currentUser={currentUser} />}
        </main>
      </div>

      {selected && (
        <DetailModal
          s={selected}
          onClose={() => setSelected(null)}
          onDelete={() => remove(selected.id)}
          onStatus={(st) => changeStatus(selected.id, st)}
        />
      )}
      {pwModalOpen && (
        <ChangePasswordModal
          token={token}
          currentUser={currentUser}
          onClose={() => setPwModalOpen(false)}
          onChanged={() => { setPwModalOpen(false); }}
        />
      )}
    </div>
  );
}

// ───── Briefings (original view, extracted) ─────
function BriefingsView({ stats, numbered, loading, q, setQ, statusFilter, setStatusFilter, onOpen, onChangeStatus }) {
  return (
    <>
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">
            Briefings
          </div>
          <h1 className="mt-2 crm-h text-4xl md:text-5xl text-[#0a0a0a]">
            Client submissions
          </h1>
        </div>
        <div className="text-xs font-mono text-[#4a4a4a]">
          {numbered.length} shown · {stats.total} total · {loading ? "loading…" : "live"}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-px bg-black/10 border border-black/10 mb-8">
        <Stat label="Total" value={stats.total} />
        <Stat label="Today" value={stats.today} />
        {STATUSES.map((s) => (
          <Stat key={s.k} label={s.label} value={stats.by_status?.[s.k] ?? 0} dot={s.dot} />
        ))}
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-5">
        <div className="flex items-center gap-2 bg-white border border-black/10 rounded-full px-4 py-2 w-full md:w-96">
          <Search className="h-4 w-4 text-[#4a4a4a]" />
          <input
            data-testid="admin-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone, company, note…"
            className="bg-transparent focus:outline-none text-sm flex-1"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              data-testid="admin-search-clear"
              className="text-xs text-[#4a4a4a] hover:text-[#ff5722]"
            >
              clear
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
          {[{ k: "all", label: "All", dot: "bg-[#0a0a0a]" }, ...STATUSES].map((s) => (
            <button
              key={s.k}
              data-testid={`admin-filter-${s.k}`}
              onClick={() => setStatusFilter(s.k)}
              className={`shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-full border text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                statusFilter === s.k
                  ? "bg-[#0a0a0a] border-[#0a0a0a] text-white"
                  : "border-black/15 text-[#4a4a4a] hover:border-[#0a0a0a]"
              }`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
              {s.label}
              {s.k !== "all" && (
                <span className="opacity-60">{stats.by_status?.[s.k] ?? 0}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {numbered.length === 0 ? (
        <div className="bg-white border border-black/10 p-14 text-center">
          <div className="font-display font-black text-3xl text-[#0a0a0a]">
            {q || statusFilter !== "all" ? "No matches." : "No briefings yet."}
          </div>
          <div className="mt-3 text-[#4a4a4a]">
            {q || statusFilter !== "all"
              ? "Try a different search or filter."
              : "When someone submits the contact form, they'll appear here."}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-black/10 overflow-x-auto">
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-4 w-12">S.No</th>
                <th className="px-4 py-4">When</th>
                <th className="px-4 py-4">Name</th>
                <th className="px-4 py-4">Email</th>
                <th className="px-4 py-4 hidden lg:table-cell">Phone</th>
                <th className="px-4 py-4 hidden lg:table-cell">Service</th>
                <th className="px-4 py-4">Status</th>
                <th className="px-4 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {numbered.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`admin-row-${s.id}`}
                  className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02] transition-colors"
                >
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a]">{String(s.sno).padStart(2, "0")}</td>
                  <td className="px-4 py-4 text-xs font-mono text-[#4a4a4a] whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="px-4 py-4 font-semibold text-[#0a0a0a] cursor-pointer" onClick={() => onOpen(s)}>{s.name}</td>
                  <td className="px-4 py-4 text-sm text-[#4a4a4a]">{s.email}</td>
                  <td className="px-4 py-4 text-sm text-[#4a4a4a] hidden lg:table-cell">{s.phone || "—"}</td>
                  <td className="px-4 py-4 text-sm hidden lg:table-cell">
                    {s.service ? (
                      <span className="px-2 py-1 text-[10px] font-mono uppercase tracking-[0.15em] bg-[#ff5722]/10 text-[#ff5722] rounded">
                        {s.service}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-4">
                    <StatusSelect value={s.status} onChange={(v) => onChangeStatus(s.id, v)} testid={`admin-status-${s.id}`} />
                  </td>
                  <td className="px-4 py-4 text-right whitespace-nowrap">
                    <button
                      onClick={() => onOpen(s)}
                      data-testid={`admin-view-${s.id}`}
                      className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-[0.15em] text-[#ff5722] hover:text-[#0a0a0a]"
                    >
                      View →
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-6 text-xs font-mono text-[#4a4a4a]">
        Stored in MongoDB · collection: <span className="text-[#0a0a0a]">contact_submissions</span>
      </p>
    </>
  );
}

// ───── Subcomponents ─────
function Stat({ label, value, dot }) {
  return (
    <div className="bg-white p-5">
      <div className="crm-num text-3xl md:text-4xl text-[#0a0a0a]">
        {value}
      </div>
      <div className="mt-2 flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
        {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </div>
    </div>
  );
}

function StatusSelect({ value, onChange, testid }) {
  const cur = STATUS_MAP[value] || STATUS_MAP.new;
  return (
    <div className="relative inline-flex items-center">
      <span className={`absolute left-2.5 h-1.5 w-1.5 rounded-full ${cur.dot}`} />
      <select
        data-testid={testid}
        value={value || "new"}
        onChange={(e) => onChange(e.target.value)}
        className="appearance-none pl-6 pr-6 py-1.5 text-xs font-mono uppercase tracking-[0.1em] bg-white border border-black/15 rounded-full hover:border-[#ff5722] focus:outline-none focus:border-[#ff5722] cursor-pointer"
      >
        {STATUSES.map((s) => <option key={s.k} value={s.k}>{s.label}</option>)}
      </select>
    </div>
  );
}

function DetailModal({ s, onClose, onDelete, onStatus }) {
  return (
    <div
      data-testid="admin-detail-modal"
      className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
    >
      <div className="relative w-full max-w-2xl bg-white border border-black/10 max-h-[90vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b border-black/10 px-6 md:px-8 py-4 flex items-center justify-between gap-3">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">
            Briefing · {fmtDate(s.created_at)}
          </div>
          <div className="flex items-center gap-2">
            <StatusSelect value={s.status} onChange={onStatus} testid="admin-detail-status" />
            <button
              onClick={onDelete}
              data-testid="admin-detail-delete"
              className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-[#4a4a4a] hover:text-[#ff5722]"
            >
              <Trash2 className="h-4 w-4" /> Delete
            </button>
            <button
              onClick={onClose}
              data-testid="admin-detail-close"
              className="h-8 w-8 rounded-full border border-black/10 hover:bg-black hover:text-white flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="p-6 md:p-10">
          <h3 className="font-display font-black text-3xl md:text-4xl tracking-tighter text-[#0a0a0a]">
            {s.name}
          </h3>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field icon={<Mail className="h-4 w-4" />} label="Email"
              value={<a className="link-underline" href={`mailto:${s.email}`}>{s.email}</a>} />
            <Field icon={<Phone className="h-4 w-4" />} label="Phone"
              value={s.phone ? <a className="link-underline" href={`tel:${s.phone}`}>{s.phone}</a> : "—"} />
            <Field icon={<Building2 className="h-4 w-4" />} label="Company" value={s.company || "—"} />
            <Field icon={<Tag className="h-4 w-4" />} label="Service" value={s.service || "—"} />
            <Field icon={<Wallet className="h-4 w-4" />} label="Budget" value={s.budget || "—"} />
          </div>
          <div className="mt-8">
            <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] mb-3">
              <MessageSquare className="h-4 w-4" /> Note
            </div>
            <div className="bg-[#f7f6f3] border border-black/10 p-5 text-[#0a0a0a] leading-relaxed whitespace-pre-wrap">
              {s.note}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a] mb-1">
        {icon} {label}
      </div>
      <div className="text-[#0a0a0a] text-sm break-all">{value}</div>
    </div>
  );
}

function ChangePasswordModal({ token, onClose, onChanged, currentUser }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be ≥ 8 characters");
    if (next !== confirmPw) return toast.error("Passwords don't match");
    try {
      setBusy(true);
      await axios.post(
        `${API}/admin/rotate-password`,
        { current_password: current, new_password: next },
        { headers: { "X-Admin-Token": token } },
      );
      toast.success("Password updated. Other sessions signed out.");
      onChanged();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Couldn't update password";
      toast.error(typeof msg === "string" ? msg : "Couldn't update password");
    } finally { setBusy(false); }
  };

  // Initials for the avatar circle
  const initials = (currentUser?.name || currentUser?.username || "R")
    .split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("");

  const isSuper = currentUser?.role === "superadmin";
  const roleLabel = isSuper ? "Super Admin" : "Founder's Office Admin";

  return (
    <div
      data-testid="admin-account-modal"
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-md flex items-center justify-center p-4"
    >
      <form
        onSubmit={submit}
        className="relative w-full max-w-md bg-white border border-black/10"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="admin-account-close"
          className="absolute top-4 right-4 h-8 w-8 rounded-full border border-black/10 hover:bg-black hover:text-white flex items-center justify-center z-10"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Profile header */}
        <div className="px-6 pt-6 pb-5 border-b border-black/10">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold">My account</div>
          <div className="mt-4 flex items-center gap-4">
            <div
              data-testid="admin-account-avatar"
              className={`h-14 w-14 rounded-full flex items-center justify-center text-white font-bold text-lg tracking-wide ${isSuper ? "bg-[#ff5722]" : "bg-blue-600"}`}
            >
              {initials}
            </div>
            <div className="min-w-0">
              <div className="font-bold text-lg text-[#0a0a0a] truncate" data-testid="admin-account-name">{currentUser?.name || "Admin"}</div>
              <div className="text-xs font-mono text-[#4a4a4a] truncate">@{currentUser?.username || "—"}</div>
              <div className="mt-1.5">
                <span data-testid="admin-account-role"
                  className={`inline-block whitespace-nowrap px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] border rounded-full ${isSuper ? "bg-[#ff5722]/10 text-[#ff5722] border-[#ff5722]/30" : "bg-blue-100 text-blue-800 border-blue-300"}`}>
                  {roleLabel}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Password change */}
        <div className="p-6 md:p-8">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#4a4a4a] font-bold">Security</div>
          <h3 className="mt-1 font-display font-black text-xl tracking-tighter text-[#0a0a0a]">Change password</h3>
          <p className="mt-1 text-xs text-[#4a4a4a]">This will sign out all your other admin sessions.</p>

          <div className="mt-5 space-y-4">
            <Pw label="Current password" value={current} onChange={setCurrent} testid="pw-current" />
            <Pw label="New password (min 8)" value={next} onChange={setNext} testid="pw-new" />
            <Pw label="Confirm new password" value={confirmPw} onChange={setConfirmPw} testid="pw-confirm" />
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-password-submit"
            className="btn-primary mt-6 w-full justify-center disabled:opacity-60"
          >
            {busy ? "Updating…" : "Update password"} <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </form>
    </div>
  );
}

function Pw({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">{label}</label>
      <input
        type="password"
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-b border-black/20 py-2 focus:outline-none focus:border-[#ff5722]"
        placeholder="••••••••"
      />
    </div>
  );
}
